import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "semantic-ui-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Category } from "../../../../../api";
import { ENV } from "../../../../../utils/constants";

import styles from "./EditCategoryPage.module.scss";

const INITIAL_VALUES = {
  id: "",
  name: "",
  slug: "",
  parent: "",
  description: "",
  order: 1,
  visible: true,
  seo: {
    metaTitle: "",
    metaDescription: "",
  },
  image: null,
  removeImage: false,
};

const MAX_IMAGE_SIZE_IN_BYTES = 5 * 1024 * 1024;

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function createSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createCategoryId(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isAcceptedImage(file) {
  return ACCEPTED_IMAGE_TYPES.includes(file?.type);
}

function getCategoryResponse(result) {
  return result?.category || result?.response?.category || result?.response || result;
}

function getCategoriesResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  return [];
}

function getCategoryId(category) {
  return String(category?._id || category?.id || "");
}

function getParentId(parent) {
  if (!parent) {
    return "";
  }

  if (typeof parent === "object") {
    return String(parent._id || parent.id || "");
  }

  return String(parent);
}

function getCategoryImage(category) {
  const imagePath = category?.image?.path;

  if (!imagePath) {
    return "";
  }

  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("blob:")
  ) {
    return imagePath;
  }

  const normalizedPath = String(imagePath)
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");

  return `${ENV.SERVER_HOST}/${normalizedPath}`;
}

function getProductCount(category) {
  if (typeof category?.productsCount === "number") {
    return category.productsCount;
  }

  if (typeof category?.productCount === "number") {
    return category.productCount;
  }

  if (typeof category?.products === "number") {
    return category.products;
  }

  if (Array.isArray(category?.products)) {
    return category.products.length;
  }

  return 0;
}

function getParentDisplayName(category) {
  const level = Math.max(Number(category?.level || 1) - 1, 0);
  const prefix = level > 0 ? `${"— ".repeat(level)}` : "";

  return `${prefix}${category?.name || ""}`;
}

function getDescendantIds(categories, parentId) {
  const descendants = [];
  const pendingParentIds = [String(parentId)];

  while (pendingParentIds.length > 0) {
    const currentParentId = pendingParentIds.shift();

    const children = categories.filter(
      (category) => getParentId(category.parent) === currentParentId
    );

    children.forEach((child) => {
      const childId = getCategoryId(child);

      if (childId) {
        descendants.push(childId);
        pendingParentIds.push(childId);
      }
    });
  }

  return descendants;
}

export function EditCategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);

  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [hasImagePreviewError, setHasImagePreviewError] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Igual que en CreateCategoryPage: mientras el usuario no haya tocado
  // "Orden" a mano, lo dejamos seguir la sugerencia automática -- pero acá
  // el valor inicial es el que ya trae la categoría, así que la
  // sugerencia solo se aplica sola cuando la persona CAMBIA la categoría
  // padre (ver el effect más abajo), nunca al cargar la página.
  const [orderTouchedByUser, setOrderTouchedByUser] = useState(false);
  const previousParentRef = useRef(null);

  const currentCategoryId = getCategoryId(category);

  const descendantIds = useMemo(() => {
    if (!currentCategoryId) {
      return [];
    }

    return getDescendantIds(allCategories, currentCategoryId);
  }, [allCategories, currentCategoryId]);

  const parentCategories = useMemo(() => {
    return allCategories.filter((item) => {
      const itemId = getCategoryId(item);

      return (
        itemId !== currentCategoryId &&
        !descendantIds.includes(itemId) &&
        Number(item.level || 1) < 3
      );
    });
  }, [allCategories, currentCategoryId, descendantIds]);

  const children = useMemo(() => {
    return allCategories
      .filter((item) => getParentId(item.parent) === currentCategoryId)
      .sort((first, second) => {
        if ((first.order || 1) !== (second.order || 1)) {
          return (first.order || 1) - (second.order || 1);
        }

        return String(first.name || "").localeCompare(
          String(second.name || "")
        );
      });
  }, [allCategories, currentCategoryId]);

  const selectedParent = useMemo(() => {
    return parentCategories.find(
      (item) => getCategoryId(item) === String(formData.parent)
    );
  }, [parentCategories, formData.parent]);

  const newCategoryLevel = formData.parent
    ? Number(selectedParent?.level || 0) + 1
    : 1;

  // El orden solo compite entre categorías HERMANAS (misma categoría
  // padre, o mismo nivel principal si no hay padre) -- excluye a la
  // propia categoría que se está editando, que si no aparecería como su
  // propia hermana e inflaría la sugerencia.
  const siblingCategories = useMemo(() => {
    return allCategories.filter(
      (item) =>
        getCategoryId(item) !== currentCategoryId &&
        getParentId(item.parent) === (formData.parent || "")
    );
  }, [allCategories, currentCategoryId, formData.parent]);

  const suggestedOrder = useMemo(() => {
    if (siblingCategories.length === 0) {
      return 1;
    }

    const maxOrder = siblingCategories.reduce(
      (max, item) => Math.max(max, Number(item.order) || 1),
      0
    );

    return maxOrder + 1;
  }, [siblingCategories]);

  const canHaveSubcategories = Number(category?.level || 1) < 3;

  const currentImage = imagePreview || existingImage;

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsLoadingCategories(true);
      setServerError("");
      setValidationErrors({});

      const categoryController = new Category();

      const [categoryResult, categoriesResult] = await Promise.all([
        categoryController.getCategory(categoryId),
        categoryController.getCategories(1, 100),
      ]);

      const categoryResponse = getCategoryResponse(categoryResult);
      const categoriesResponse = getCategoriesResponse(categoriesResult);

      if (!categoryResponse?._id && !categoryResponse?.id) {
        throw {
          msg: "No fue posible obtener la información de la categoría.",
        };
      }

      setCategory(categoryResponse);
      setAllCategories(categoriesResponse);
      setExistingImage(getCategoryImage(categoryResponse));
      setImagePreview("");
      setHasImagePreviewError(false);

      setFormData({
        id: categoryResponse.id || "",
        name: categoryResponse.name || "",
        slug: categoryResponse.slug || "",
        parent: getParentId(categoryResponse.parent),
        description: categoryResponse.description || "",
        order: Number(categoryResponse.order) || 1,
        visible: categoryResponse.visible ?? true,
        seo: {
          metaTitle: categoryResponse.seo?.metaTitle || "",
          metaDescription: categoryResponse.seo?.metaDescription || "",
        },
        image: null,
        removeImage: false,
      });
    } catch (errorResponse) {
      console.error("loadData error:", errorResponse);

      setCategory(null);
      setAllCategories([]);

      setServerError(
        errorResponse?.msg ||
          "No fue posible cargar la categoría. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
      setIsLoadingCategories(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  // Solo re-sugiere el orden cuando la categoría padre CAMBIA de verdad
  // (reasignar la categoría a otro padre vuelve obsoleto el orden que
  // traía, porque competía con otro grupo de hermanas) -- nunca en la
  // carga inicial, donde previousParentRef todavía no tiene una base con
  // la cual comparar.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const parentChanged =
      previousParentRef.current !== null &&
      previousParentRef.current !== formData.parent;

    previousParentRef.current = formData.parent;

    if (!parentChanged || orderTouchedByUser) {
      return;
    }

    setFormData((currentData) => ({
      ...currentData,
      order: suggestedOrder,
    }));
  }, [formData.parent, suggestedOrder, orderTouchedByUser, isLoading]);

  function clearFieldError(fieldName) {
    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;

    if (name === "metaTitle" || name === "metaDescription") {
      setFormData((currentData) => ({
        ...currentData,
        seo: {
          ...currentData.seo,
          [name]: value,
        },
      }));

      clearFieldError(name);
      return;
    }

    if (name === "order") {
      setOrderTouchedByUser(true);
    }

    setFormData((currentData) => ({
      ...currentData,
      [name]: type === "checkbox" ? checked : value,
    }));

    clearFieldError(name);
  }

  function handleUseSuggestedOrder() {
    setOrderTouchedByUser(false);

    setFormData((currentData) => ({
      ...currentData,
      order: suggestedOrder,
    }));

    clearFieldError("order");
  }

  function handleNameChange(event) {
    const name = event.target.value;

    setFormData((currentData) => ({
      ...currentData,
      name,
      id: currentData.id || createCategoryId(name),
      slug: currentData.slug || createSlug(name),
    }));

    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      name: "",
      id: "",
      slug: "",
    }));
  }

  function handleOpenImagePicker() {
    if (!isSubmitting) {
      fileInputRef.current?.click();
    }
  }

  function handleImageChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!isAcceptedImage(file)) {
      setServerError("La imagen debe estar en formato JPG, PNG o WEBP.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_IN_BYTES) {
      setServerError("La imagen no puede superar 5 MB.");
      event.target.value = "";
      return;
    }

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    const previewUrl = URL.createObjectURL(file);

    setImagePreview(previewUrl);
    setExistingImage("");
    setHasImagePreviewError(false);

    setFormData((currentData) => ({
      ...currentData,
      image: file,
      removeImage: false,
    }));

    setServerError("");
    clearFieldError("image");
  }

  function handleImagePreviewError() {
    setHasImagePreviewError(true);
  }

  function handleRemoveImage() {
    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview("");
    setExistingImage("");
    setHasImagePreviewError(false);

    setFormData((currentData) => ({
      ...currentData,
      image: null,
      removeImage: true,
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    clearFieldError("image");
  }

  function validateForm() {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "El nombre de la categoría es obligatorio.";
    } else if (formData.name.trim().length > 150) {
      errors.name = "El nombre no puede superar 150 caracteres.";
    }

    if (!formData.slug.trim()) {
      errors.slug = "El slug o URL de la categoría es obligatorio.";
    } else if (createSlug(formData.slug).length > 160) {
      errors.slug = "El slug no puede superar 160 caracteres.";
    }

    if (
      !Number.isInteger(Number(formData.order)) ||
      Number(formData.order) < 1
    ) {
      errors.order =
        "El orden debe ser un número entero mayor o igual a 1.";
    }

    if (formData.parent && !selectedParent) {
      errors.parent = "La categoría padre seleccionada no está disponible.";
    }

    if (formData.parent && newCategoryLevel > 3) {
      errors.parent = "No se permite superar 3 niveles de categorías.";
    }

    if (formData.description.length > 1000) {
      errors.description =
        "La descripción no puede superar 1,000 caracteres.";
    }

    if (formData.seo.metaTitle.length > 60) {
      errors.metaTitle =
        "El título SEO no puede superar 60 caracteres.";
    }

    if (formData.seo.metaDescription.length > 160) {
      errors.metaDescription =
        "La descripción meta no puede superar 160 caracteres.";
    }

    if (formData.image && !isAcceptedImage(formData.image)) {
      errors.image = "La imagen debe estar en formato JPG, PNG o WEBP.";
    }

    if (
      formData.image &&
      formData.image.size > MAX_IMAGE_SIZE_IN_BYTES
    ) {
      errors.image = "La imagen no puede superar 5 MB.";
    }

    return errors;
  }

  async function handleSubmit(event) {
  event.preventDefault();

  const errors = validateForm();

  if (Object.keys(errors).length > 0) {
    setValidationErrors(errors);
    setServerError("");
    return;
  }

  try {
    setIsSubmitting(true);
    setServerError("");
    setSuccessMessage("");

    const categoryController = new Category();

    await categoryController.updateCategory(categoryId, {
      ...formData,
      id: formData.id.trim() || createCategoryId(formData.name),
      slug: createSlug(formData.slug || formData.name),
      parent: formData.parent || null,
      order: Number(formData.order),
    });

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    navigate("/admin/categories", {
      replace: true,
      state: {
        success: "Categoría actualizada correctamente.",
      },
    });
  } catch (errorResponse) {
    console.error("updateCategory error:", errorResponse);

    if (errorResponse?.errors) {
      setValidationErrors(errorResponse.errors);
    }

    setServerError(
      errorResponse?.msg ||
        "No fue posible actualizar la categoría. Intenta nuevamente."
    );
  } finally {
    setIsSubmitting(false);
  }
}

  function handleCancel() {
    if (!isSubmitting) {
      navigate("/admin/categories");
    }
  }

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  if (isLoading) {
    return (
      <section className={styles.EditCategoryPage}>
        <div className={styles.LoadingState}>Cargando categoría...</div>
      </section>
    );
  }

  if (!category) {
    return (
      <section className={styles.EditCategoryPage}>
        <div className={styles.ErrorState}>
          <p>{serverError || "Categoría no encontrada."}</p>

          <Link
            to="/admin/categories"
            className={styles.BackToListButton}
          >
            Volver a categorías
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.EditCategoryPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Volver a categorías"
          >
            ←
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Categorías / {category.name}
            </p>

            <div className={styles.TitleRow}>
              <h1>{category.name}</h1>

              <span
                className={
                  category.visible
                    ? styles.VisibleBadge
                    : styles.HiddenBadge
                }
              >
                {category.visible ? "Visible" : "Oculta"}
              </span>
            </div>

            <p className={styles.Subtitle}>
              Edita la información, jerarquía, visibilidad y configuración SEO
              de esta categoría.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            form="edit-category-form"
            className={styles.PublishButton}
            disabled={isSubmitting || isLoadingCategories}
          >
            {isSubmitting ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </header>

      {hasValidationErrors && (
        <div className={styles.ValidationMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <div>
            <strong>Revisa los campos requeridos.</strong>

            <span>
              Corrige los errores marcados antes de guardar los cambios.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <span>{serverError}</span>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          <span className={styles.SuccessIcon} aria-hidden="true">
            ✓
          </span>

          <span>{successMessage}</span>
        </div>
      )}

      <form
        id="edit-category-form"
        className={styles.FormContent}
        onSubmit={handleSubmit}
      >
        <section className={styles.CategoryBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información básica</h2>

              <p>Actualiza los datos principales de la categoría.</p>
            </div>
          </header>

          <div className={styles.InfoMessage}>
            <span className={styles.InfoIcon}>i</span>

            <p>
              Puedes cambiar la categoría padre. El sistema no permite ciclos
              ni más de 3 niveles de profundidad.
            </p>
          </div>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label htmlFor="name">
                Nombre <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Ej. Bolsas de mano"
                error={Boolean(validationErrors.name)}
                disabled={isSubmitting}
              />

              {validationErrors.name && (
                <span className={styles.FieldError}>
                  {validationErrors.name}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="slug">
                Slug / URL <span>*</span>
              </label>

              <Input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="Ej. bolsas-de-mano"
                error={Boolean(validationErrors.slug)}
                disabled={isSubmitting}
              />

              {validationErrors.slug ? (
                <span className={styles.FieldError}>
                  {validationErrors.slug}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Se usará en la URL de la tienda.
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="parent">Categoría padre</label>

              <select
                id="parent"
                name="parent"
                value={formData.parent}
                onChange={handleChange}
                className={validationErrors.parent ? styles.error : ""}
                disabled={isSubmitting || isLoadingCategories}
              >
                <option value="">
                  {isLoadingCategories
                    ? "Cargando categorías..."
                    : "Sin categoría padre (categoría principal)"}
                </option>

                {parentCategories.map((parentCategory) => (
                  <option
                    key={getCategoryId(parentCategory)}
                    value={getCategoryId(parentCategory)}
                  >
                    {getParentDisplayName(parentCategory)}
                  </option>
                ))}
              </select>

              {validationErrors.parent ? (
                <span className={styles.FieldError}>
                  {validationErrors.parent}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  {formData.parent
                    ? `Esta categoría quedará en nivel ${newCategoryLevel}.`
                    : "Esta categoría será principal."}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="image">Imagen portada</label>

              <div className={styles.ImageUpload}>
                {currentImage ? (
                  <div className={styles.ImagePreview}>
                    {!hasImagePreviewError ? (
                      <img
                        src={currentImage}
                        alt={`Imagen de ${formData.name || "categoría"}`}
                        onError={handleImagePreviewError}
                      />
                    ) : (
                      <div className={styles.ImageFallback}>
                        <span>▣</span>
                        <p>No fue posible mostrar la imagen.</p>
                      </div>
                    )}

                    <div className={styles.ImageActions}>
                      <button
                        type="button"
                        className={styles.ReplaceImageButton}
                        onClick={handleOpenImagePicker}
                        disabled={isSubmitting}
                      >
                        Reemplazar imagen
                      </button>

                      <button
                        type="button"
                        className={styles.RemoveImageButton}
                        onClick={handleRemoveImage}
                        disabled={isSubmitting}
                      >
                        Eliminar imagen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.UploadLabel}
                    onClick={handleOpenImagePicker}
                    disabled={isSubmitting}
                  >
                    <span>↥</span>
                    <span>Selecciona una imagen</span>
                    <strong>Subir imagen</strong>
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                id="image"
                name="image"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={isSubmitting}
                hidden
              />

              {validationErrors.image ? (
                <span className={styles.FieldError}>
                  {validationErrors.image}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  JPG, PNG o WEBP. Máximo 5 MB. Recomendado: 1200 × 1200 px.
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={
                  validationErrors.description ? styles.error : ""
                }
                placeholder="Describe la categoría y el tipo de productos que contiene..."
                maxLength="1000"
                disabled={isSubmitting}
              />

              <div className={styles.TextAreaFooter}>
                {validationErrors.description ? (
                  <span className={styles.FieldError}>
                    {validationErrors.description}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formData.description.length} / 1000</span>
              </div>
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden <span>*</span>
              </label>

              <Input
                id="order"
                name="order"
                type="number"
                min="1"
                step="1"
                value={formData.order}
                onChange={handleChange}
                error={Boolean(validationErrors.order)}
                disabled={isSubmitting}
              />

              {validationErrors.order ? (
                <span className={styles.FieldError}>
                  {validationErrors.order}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Menor número = aparece primero. Siguiente disponible
                  {formData.parent
                    ? " en esta categoría padre"
                    : " entre las categorías principales"}
                  : {suggestedOrder}.{" "}
                  {Number(formData.order) !== suggestedOrder && (
                    <button
                      type="button"
                      className={styles.UseSuggestedOrderButton}
                      onClick={handleUseSuggestedOrder}
                      disabled={isSubmitting}
                    >
                      Usar {suggestedOrder}
                    </button>
                  )}
                </span>
              )}
            </div>

            <div className={styles.VisibilityField}>
              <span>Visible</span>

              <label className={styles.VisibilitySwitch}>
                <input
                  name="visible"
                  type="checkbox"
                  checked={formData.visible}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />

                <span />
              </label>

              <small>
                {formData.visible
                  ? "La categoría se mostrará en la tienda."
                  : "La categoría no se mostrará en la tienda."}
              </small>
            </div>

            <section className={`${styles.SeoSection} ${styles.FullWidth}`}>
              <h3 className={styles.SeoTitle}>SEO</h3>

              <p className={styles.SeoDescription}>
                Optimiza cómo se mostrará esta categoría en buscadores.
              </p>

              <div className={styles.SeoGrid}>
                <div className={styles.FormField}>
                  <label htmlFor="metaTitle">Título SEO</label>

                  <Input
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.seo.metaTitle}
                    onChange={handleChange}
                    error={Boolean(validationErrors.metaTitle)}
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaTitle ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaTitle}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>{formData.seo.metaTitle.length} / 60</span>
                  </div>
                </div>

                <div className={styles.FormField}>
                  <label htmlFor="metaDescription">
                    Descripción meta
                  </label>

                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.seo.metaDescription}
                    onChange={handleChange}
                    className={
                      validationErrors.metaDescription
                        ? styles.error
                        : ""
                    }
                    maxLength="160"
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaDescription ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaDescription}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>
                      {formData.seo.metaDescription.length} / 160
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </form>

      <section className={styles.SubcategoriesSection}>
        <header className={styles.SubcategoriesHeader}>
          <div>
            <h2>Subcategorías</h2>

            <p>
              Categorías directamente asociadas a{" "}
              <strong>{category.name}</strong>.
            </p>
          </div>

          <span className={styles.SubcategoriesCount}>
            {children.length}{" "}
            {children.length === 1 ? "subcategoría" : "subcategorías"}
          </span>
        </header>

        {!canHaveSubcategories ? (
          <div className={styles.SubcategoriesEmpty}>
            Esta categoría está en el nivel máximo permitido y no puede tener
            subcategorías.
          </div>
        ) : children.length === 0 ? (
          <div className={styles.SubcategoriesEmpty}>
            Esta categoría aún no tiene subcategorías.
          </div>
        ) : (
          <div className={styles.SubcategoriesList}>
            {children.map((child) => {
              const childId = getCategoryId(child);
              const childImage = getCategoryImage(child);

              return (
                <Link
                  key={childId}
                  to={`/admin/categories/${childId}/edit`}
                  className={styles.SubcategoryItem}
                >
                  {childImage ? (
                    <img
                      src={childImage}
                      alt=""
                      className={styles.SubcategoryImage}
                    />
                  ) : (
                    <span className={styles.SubcategoryIcon}>▪</span>
                  )}

                  <span className={styles.SubcategoryContent}>
                    <strong>{child.name}</strong>

                    <small>
                      Orden: {child.order || 1} · Productos:{" "}
                      {getProductCount(child)}
                    </small>
                  </span>

                  <span
                    className={
                      child.visible
                        ? styles.ChildVisibleBadge
                        : styles.ChildHiddenBadge
                    }
                  >
                    {child.visible ? "Visible" : "Oculta"}
                  </span>

                  <span className={styles.SubcategoryArrow}>›</span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
}